<template>
    <div :class="getPageContainerClasses">
        <form id="customer-login" method="POST" :action="getFormAction" @submit="onSubmit">
            <input type="hidden" name="_token" :value="csrfToken">
            <input type="hidden" name="email" :value="email">
            
            <div id="partner-login">
                <!-- Success message -->
                <div v-if="displaySuccessMessage" class="mb-6">
                    <div class="bg-green-50 border border-green-100 text-green-800 rounded text-sm p-4">
                        {{ displaySuccessMessage }}
                    </div>
                </div>

                <!-- Form errors -->
                <form-errors :store-form-errors="formErrors"/>

                <!-- Email display (disabled) -->
                <div class="mb-6 space-y-4">
                    <div>
                        <editable-text-field
                            v-model="portalContentAndDesignStore.translationSettings.portal_translation_login_email"
                            for-attribute="email_display"
                            class="block text-sm opacity-80 mb-1.5"
                            :class="getPageContentTextColorClass"
                            text-type="label"
                        >
                        </editable-text-field>
                        <input
                            type="email"
                            name="email_display"
                            id="email_display"
                            :value="email"
                            disabled
                            :class="getEmailInputClasses"
                        >
                    </div>

                    <!-- OTP Token Input -->
                    <div>
                        <editable-text-field
                            :model-value="otpLabelText"
                            @update:model-value="updateOtpLabel"
                            for-attribute="token"
                            class="block text-sm opacity-80 mb-1.5"
                            :class="getPageContentTextColorClass"
                            text-type="label"
                        >
                        </editable-text-field>
                        <input
                            type="password"
                            name="token"
                            id="token"
                            required
                            autocomplete="one-time-code"
                            :class="getOtpInputClasses"
                            @keyup.enter="submitForm"
                        >
                    </div>
                </div>

                <editable-btn
                    button-type="submit"
                    :prevent-double-click="5000"
                    size="huge"
                    :class="getButtonClasses"
                    :type="getButtonType"
                    v-model="portalContentAndDesignStore.translationSettings.portal_translation_login_login"
                >
                    {{ portalContentAndDesignStore.translationSettings.portal_translation_login_login }}
                </editable-btn>

                <!-- Go back to login link -->
                <div class="mt-4 text-center">
                    <a
                        :href="portalContentAndDesignStore.isPreview ? '#' : portalContentAndDesignStore.getLoginRoute"
                        class="text-sm hover:underline"
                        :class="getPageLinkColorClass"
                    >
                        {{ goBackToLoginText }}
                    </a>
                </div>
            </div>
        </form>
    </div>
</template>

<script setup>
import {computed, ref} from "vue";
import {usePortalContentAndDesignStore} from "../../../../../../stores/partners/portalContentAndDesignStore";
import FormErrors from "../../../../Pub/Pages/Errors/FormErrors.vue";
// editable-btn, editable-text-field are registered globally

const props = defineProps({
    email: {
        type: String,
        required: true
    },
    successMessage: {
        type: String,
        default: ''
    }
});

const portalContentAndDesignStore = usePortalContentAndDesignStore();

// Form errors state
const formErrors = ref({
    validationErrors: null,
    responseError: null
});

const csrfToken = computed(() => {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
});

const getFormAction = computed(() => {
    // For referral portals OTP login, use the standard OTP login route
    return '/otp/login';
});

// Referral portal specific container classes
const getPageContainerClasses = computed(() => {
    const style = portalContentAndDesignStore.portalLayoutSettings?.referral_portal_content_layout_style || 'border';
    const corners = portalContentAndDesignStore.portalLayoutSettings?.referral_portal_content_layout_corners || 'rounded-xl';
    const backgroundColor = portalContentAndDesignStore.portalLayoutSettings?.referral_portal_content_background_color || 'white';
    const borderColor = portalContentAndDesignStore.portalLayoutSettings?.referral_portal_content_border_color || 'gray-200';
    
    const classes = ['px-6 lg:px-12 py-8 lg:py-14'];
    
    // Background color
    if (backgroundColor.startsWith('#')) {
        classes.push('bg-content-custom-bg');
    } else {
        classes.push(`bg-${backgroundColor}`);
    }
    
    // Layout style
    if (style === 'border') {
        if (borderColor.startsWith('#')) {
            classes.push('border border-content-custom-color');
        } else {
            classes.push(`border border-${borderColor}`);
        }
    } else if (style === 'shadow') {
        classes.push('shadow-lg');
    }
    
    // Corners
    if (corners === 'none') {
        classes.push('rounded-sm');
    } else {
        classes.push(corners);
    }
    
    return classes.join(' ');
});

// Referral portal specific text color class
const getPageContentTextColorClass = computed(() => {
    const textColor = portalContentAndDesignStore.portalLayoutSettings?.referral_portal_content_text_color || 'gray-800';
    
    if (textColor.startsWith('#')) {
        return 'text-content-custom-text';
    } else {
        return `text-${textColor}`;
    }
});

// Email input styling based on referral portal settings (for disabled email input)
const getEmailInputClasses = computed(() => {
    const inputStyle = portalContentAndDesignStore.portalLayoutSettings?.referral_portal_content_input_style || 'white';
    const corners = portalContentAndDesignStore.portalLayoutSettings?.referral_portal_content_layout_corners || 'rounded-xl';
    const textColor = portalContentAndDesignStore.portalLayoutSettings?.referral_portal_content_text_color || 'gray-800';
    
    const classes = [
        'transition-shadow block w-full text-sm px-3.5 py-3.5',
        'border-0 ring-1 ring-inset',
        'disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed disabled:opacity-75'
    ];
    
    // Input style (background and border)
    if (inputStyle === 'white') {
        classes.push('bg-white text-gray-900 ring-gray-300');
    } else {
        if (textColor.startsWith('#')) {
            classes.push('bg-transparent text-content-custom-text ring-content-custom-text');
        } else {
            classes.push(`bg-transparent text-${textColor} ring-${textColor}`);
        }
    }
    
    // Corners
    if (corners === 'none') {
        classes.push('rounded');
    } else {
        classes.push(corners);
    }
    
    return classes.join(' ');
});

// OTP input styling (for enabled OTP input)
const getOtpInputClasses = computed(() => {
    const inputStyle = portalContentAndDesignStore.portalLayoutSettings?.referral_portal_content_input_style || 'white';
    const corners = portalContentAndDesignStore.portalLayoutSettings?.referral_portal_content_layout_corners || 'rounded-xl';
    const textColor = portalContentAndDesignStore.portalLayoutSettings?.referral_portal_content_text_color || 'gray-800';
    
    const classes = [
        'transition-shadow block w-full text-sm px-3.5 py-3.5',
        'border-0 ring-1 ring-inset'
    ];
    
    // Input style (background and border)
    if (inputStyle === 'white') {
        classes.push('bg-white text-gray-900 ring-gray-300 focus:ring-gray-400');
    } else {
        if (textColor.startsWith('#')) {
            classes.push('bg-transparent text-content-custom-text ring-content-custom-text focus:ring-content-custom-text');
        } else {
            classes.push(`bg-transparent text-${textColor} ring-${textColor} focus:ring-${textColor}`);
        }
    }
    
    // Corners
    if (corners === 'none') {
        classes.push('rounded');
    } else {
        classes.push(corners);
    }
    
    return classes.join(' ');
});

// Button styling based on referral portal settings
const getButtonClasses = computed(() => {
    const corners = portalContentAndDesignStore.portalLayoutSettings?.referral_portal_content_layout_corners || 'rounded-xl';
    
    const classes = ['w-full'];
    
    // Corners
    if (corners === 'none') {
        classes.push('rounded');
    } else {
        classes.push(corners);
    }
    
    return classes.join(' ');
});

const getButtonType = computed(() => {
    const buttonColor = portalContentAndDesignStore.portalLayoutSettings?.referral_portal_content_button_color || 'teal';
    
    if (buttonColor.startsWith('#')) {
        return 'custom-content';
    } else {
        return buttonColor;
    }
});

// Link color class
const getPageLinkColorClass = computed(() => {
    const linkColor = portalContentAndDesignStore.portalLayoutSettings?.referral_portal_content_link_color || 'teal-600';
    
    if (linkColor.startsWith('#')) {
        return 'text-content-custom-link';
    } else {
        return `text-${linkColor}`;
    }
});

// Go back to login text
const goBackToLoginText = computed(() => {
    return portalContentAndDesignStore.translationSettings.portal_translation_login_go_back_to_login || 'Go back to login';
});

// Success message with translation support
const displaySuccessMessage = computed(() => {
    // First try to get the translated message from the store
    const translatedMessage = portalContentAndDesignStore.translationSettings?.portal_translation_otp_token_sent;
    
    // If translation exists and is not empty, use it
    if (translatedMessage && translatedMessage.trim() !== '') {
        return translatedMessage;
    }
    
    // Otherwise, fall back to the successMessage prop from backend
    return props.successMessage || '';
});

// OTP label with fallback
const otpLabelText = computed(() => {
    return portalContentAndDesignStore.translationSettings.portal_translation_login_OTP || 'One-time password';
});

const updateOtpLabel = (value) => {
    portalContentAndDesignStore.translationSettings.portal_translation_login_OTP = value;
};

const submitForm = () => {
    // Trigger form submission when Enter is pressed in token field
    document.getElementById('customer-login')?.submit();
};

const onSubmit = (event) => {
    // For referral portals, let the form submit normally to the backend
    if (portalContentAndDesignStore.isPreview) {
        event.preventDefault();
        return;
    }
    
    // Check if token is filled
    const tokenInput = document.getElementById('token');
    
    if (!tokenInput?.value) {
        event.preventDefault();
        alert('Please enter your OTP token');
        return;
    }
    
    // Allow normal form submission
};
</script>
